import { NegotiationsReportItem } from "../../../api";
import type { FilterFieldKey, NegotiationsFiltersState } from "./types";
import { EMPTY_FILTERS } from "./types";

export function uniqueFilterOptions(
  rows: NegotiationsReportItem[],
  field: FilterFieldKey
): string[] {
  if (field === "overdue" || field === "searchTerm") {
    return [];
  }

  const values = new Set<string>();
  rows.forEach((row) => {
    const value = String(row[field as keyof NegotiationsReportItem] ?? "").trim();
    if (value) values.add(value);
  });
  return [...values].sort((a, b) => a.localeCompare(b));
}

export function matchesSearchTerm(row: NegotiationsReportItem, searchTerm: string): boolean {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return true;

  return (
    (row.caseName?.toLowerCase().includes(term) ?? false) ||
    (row.raa?.toLowerCase().includes(term) ?? false) ||
    (row.taskName?.toLowerCase().includes(term) ?? false) ||
    (row.taskAssignee?.toLowerCase().includes(term) ?? false) ||
    (row.status?.toLowerCase().includes(term) ?? false)
  );
}

export function applySearchFilter(
  rows: NegotiationsReportItem[],
  searchTerm: string
): NegotiationsReportItem[] {
  if (!searchTerm.trim()) return rows;
  return rows.filter((row) => matchesSearchTerm(row, searchTerm));
}

export function mergeUniqueOptions(...lists: Array<string[] | undefined>): string[] {
  const values = new Set<string>();
  lists.forEach((list) => {
    list?.forEach((value) => {
      const trimmed = value.trim();
      if (trimmed) values.add(trimmed);
    });
  });
  return [...values].sort((a, b) => a.localeCompare(b));
}

export function countActiveFilters(filters: NegotiationsFiltersState): number {
  let count = 0;
  (Object.keys(filters) as (keyof NegotiationsFiltersState)[]).forEach((key) => {
    const value = filters[key];
    if (
      key === "dueDateFrom" ||
      key === "dueDateTo" ||
      key === "taskedFrom" ||
      key === "taskedTo" ||
      key === "negotiationStartedFrom" ||
      key === "negotiationStartedTo"
    ) {
      if (value != null) count += 1;
      return;
    }
    if (typeof value === "string" && value.trim()) count += 1;
  });
  return count;
}

export function hasActiveFilters(filters: NegotiationsFiltersState): boolean {
  return countActiveFilters(filters) > 0;
}

export function clearFilterField(
  filters: NegotiationsFiltersState,
  key: keyof NegotiationsFiltersState
): NegotiationsFiltersState {
  if (
    key === "dueDateFrom" ||
    key === "dueDateTo" ||
    key === "taskedFrom" ||
    key === "taskedTo" ||
    key === "negotiationStartedFrom" ||
    key === "negotiationStartedTo"
  ) {
    return { ...filters, [key]: null };
  }
  return { ...filters, [key]: "" };
}

export function resetFilters(): NegotiationsFiltersState {
  return { ...EMPTY_FILTERS };
}
